import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { FieldLabel } from './FieldLabel';

/**
 * How much horizontal room the input box gets.
 *
 * A box's width is a claim about how much data belongs in it. `w-full` on a
 * field that accepts two digits is a lie the user reads before they read the
 * label — it invites a sentence and then rejects everything past "25". The
 * rungs are named for the SHAPE of the answer, never for a pixel count, so a
 * caller cannot invent a fourth width by hand.
 */
/** The indigo focus ring every editorial text input shares (#3169): one spelling, reused by the MFA code box. */
export const EDITORIAL_FOCUS_RING = 'focus:outline-none focus:border-stevens-indigo focus:shadow-[0_0_0_3px_color-mix(in_oklab,var(--stevens-indigo)_18%,transparent)]';

export type FieldWidth = 'full' | 'numeric' | 'short';

/** Bare box: the input holds only its own value. */
const WIDTH_CLASS: Record<FieldWidth, string> = {
  full: 'w-full',
  numeric: 'w-20',
  short: 'w-48',
};

/**
 * The same rungs widened by exactly the unit gutter (`UNIT_GUTTER_CLASS`), so
 * adding a unit never steals typing room. `full` is already the whole line and
 * has nothing to widen into.
 */
const WIDTH_WITH_UNIT_CLASS: Record<FieldWidth, string> = {
  full: 'w-full',
  numeric: 'w-32',
  short: 'w-64',
};

/**
 * Right padding reserved for the unit so typed text can never slide under it.
 * 64px holds the 16px trailing inset plus a short word at the 13px rung —
 * "years", "months", "%". A unit is a word, not a phrase; anything longer
 * belongs in the label or the helper line.
 */
const UNIT_GUTTER_CLASS = 'pr-16';

interface EditorialFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  helper?: ReactNode;
  error?: string;
  /** Rendered beside the LABEL, on the label row — not inside the box. */
  trailing?: ReactNode;
  /** Box width. Defaults to `full`, which is the pre-existing behaviour. */
  width?: FieldWidth;
  /**
   * Short unit word painted inside the box on the trailing edge ("years").
   * It is `aria-hidden` on purpose: a screen reader user gets the unit from
   * the label, and hearing it twice is noise. So the LABEL must still name the
   * unit — "Child's age (years)", not "Child's age".
   */
  unit?: string;
}

export const EditorialField = forwardRef<HTMLInputElement, EditorialFieldProps>(
  function EditorialField(
    { label, helper, error, trailing, width = 'full', unit, className, id, required, ...rest },
    ref,
  ) {
    const inputId = id ?? `field-${label.replace(/\s+/g, '-').toLowerCase()}`;
    const helperId = helper || error ? `${inputId}-help` : undefined;
    // The box width lives on the WRAPPER, not the input: the unit is absolutely
    // positioned against that wrapper, so the wrapper has to be the thing whose
    // trailing edge the unit sits on. The input stays `w-full` inside it, which
    // is also why a caller's own `w-*` in `className` still wins as it did.
    const boxWidthClass = unit ? WIDTH_WITH_UNIT_CLASS[width] : WIDTH_CLASS[width];
    // Digits in a bounded box have to line up column-wise; proportional figures
    // make "11" and "25" different widths and the field look unsteady as you type.
    const numericFigures = Boolean(unit) || width === 'numeric';

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-3">
          <FieldLabel htmlFor={inputId} required={required}>{label}</FieldLabel>
          {trailing}
        </div>
        <div className={cn('relative', boxWidthClass)}>
          <input
            ref={ref}
            id={inputId}
            required={required}
            aria-required={required || undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={helperId}
            className={cn(
              'w-full rounded-card border border-stevens-rule bg-stevens-surface px-4 py-3 font-sans text-body leading-[1.5] text-stevens-ink placeholder:text-stevens-ink-placeholder',
              'transition-[border-color,box-shadow] duration-150',
              EDITORIAL_FOCUS_RING,
              'disabled:opacity-50 disabled:cursor-not-allowed',
              numericFigures && 'tabular-nums',
              unit && UNIT_GUTTER_CLASS,
              error && 'border-[oklch(60%_0.18_25)] focus:border-[oklch(60%_0.18_25)] focus:shadow-[0_0_0_3px_color-mix(in_oklab,oklch(60%_0.18_25)_18%,transparent)]',
              className,
            )}
            {...rest}
          />
          {unit ? (
            // `pointer-events-none` so a click on the word still lands on the
            // input and focuses it — the unit is paint, not a target.
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 font-sans text-body-sm leading-none text-stevens-ink-3"
            >
              {unit}
            </span>
          ) : null}
        </div>
        {error ? (
          <p id={helperId} className="text-body-sm leading-[1.5] text-[oklch(55%_0.18_25)]">
            {error}
          </p>
        ) : helper ? (
          <p id={helperId} className="text-body-sm leading-[1.5] text-stevens-ink-3">
            {helper}
          </p>
        ) : null}
      </div>
    );
  },
);
